import React from 'react';
import {faAngleDown} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import type {PaletteMode} from '@mui/material';
import type {Components, Theme} from '@mui/material/styles';
import {defaultMuiTheme} from '../defaults';
import {getOutlinedBorderColor} from './getOutlinedBorderColor';
import {getPlaceholderOpacity} from './getPlaceholderOpacity';
import {getStylePropertyFn} from './getStylePropertyFn';

export const getComponentOverrides = (
	mode: PaletteMode,
	opts?: {demo?: boolean},
): Components<Omit<Theme, 'components'>> => {
	const getStyleProperty = getStylePropertyFn(mode, opts);
	const outlinedBorderColor = getOutlinedBorderColor(mode);
	const placeholderVisible = getPlaceholderOpacity(mode);

	const light = mode === 'light';

	return {
		MuiAccordionSummary: {
			defaultProps: {
				expandIcon: <FontAwesomeIcon icon={faAngleDown} />,
			},
			styleOverrides: {
				expandIconWrapper: {
					color: getStyleProperty('text-primary-on-background'),
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				root: {
					fontSize: '14px',
				},
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				root: {
					// Show pointer cursor if not focused
					'& [class*="MuiInputBase-root"]:not([class*="Mui-focused"])': {
						'&, & input': {
							cursor: 'pointer',
						},
					},
					'&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
						paddingRight: 42,
					},
					'& [class*="MuiInputLabel-root"]': {
						maxWidth: 'calc(100% - 56px)',
						'&:not([class*="MuiInputLabel-outlined"]):not([class*="MuiInputLabel-filled"])':
							{
								paddingLeft: 16,
								'& + .MuiInput-root .MuiInput-input': {
									paddingLeft: 16,
								},
								'&[class*="MuiInputLabel-shrink"]': {
									paddingLeft: 22,
								},
							},
					},
					'&.SelectField-multiple': {
						'& .MuiInputLabel-root': {
							'&.MuiInputLabel-outlined[data-shrink="true"]': {
								transform: 'translate(14px, -5px) scale(0.75)',
							},
						},
					},
				},
				groupUl: {
					'& .MuiAutocomplete-option': {
						paddingLeft: defaultMuiTheme.spacing(2),
					},
				},
				noOptions: {
					fontSize: 16,
				},
			},
		},
		MuiAvatar: {
			styleOverrides: {
				root: {
					// Fix: in safari the image escapes the border-radius overflow until the transition
					// is complete
					WebkitMaskImage: '-webkit-radial-gradient(white, black)',
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					flex: '0 0 auto',
					// Messes with translations
					textTransform: 'unset',
					'& p': {
						color: 'inherit',
					},
				},
				iconSizeMedium: {
					'& > *:first-of-type.svg-inline--fa': {
						fontSize: 16,
					},
				},
			},
		},
		MuiButtonBase: {
			styleOverrides: {
				root: {
					fontSize: '24px',
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				sizeMedium: {
					'& svg': {
						width: 24,
						height: 24,
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					maxWidth: '100%',
					overflow: 'hidden',
					'& span': {
						display: 'inline-block',
						flex: '1 1 auto',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					},
					'& svg': {
						flex: '0 0 auto',
					},
				},
			},
		},
		MuiFilledInput: {
			defaultProps: {},
			styleOverrides: {
				root: {
					backgroundColor: light
						? `rgba(0, 0, 0, 0.025)`
						: `rgba(255, 255, 255, 0.04)`,
					'&:hover': {
						backgroundColor: light
							? `rgba(0, 0, 0, 0.04)`
							: `rgba(255, 255, 255, 0.08)`,
					},
					'&$focused': {
						backgroundColor: light
							? `rgba(0, 0, 0, 0.04)`
							: `rgba(255, 255, 255, 0.08)`,
					},
				},
			},
		},
		MuiFormHelperText: {
			defaultProps: {
				variant: 'outlined',
			},
			styleOverrides: {
				root: {
					fontSize: '12px',
				},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					'&:not([class*="MuiSwitch-switchBase"]) svg:not(.svg-inline--fa)': {
						minHeight: 24,
						minWidth: 24,
					},
					// FontAwesome icons are just too dang big
					'& .svg-inline--fa': {
						height: 16,
						width: 16,
					},
				},
				edgeEnd: {
					marginRight: -defaultMuiTheme.spacing(2),
				},
			},
		},
		MuiInputLabel: {
			defaultProps: {
				shrink: true,
			},
			styleOverrides: {
				root: {
					maxWidth: 'calc(100% - 20px)',
					textOverflow: 'ellipsis',
					overflow: 'hidden',
					wordBreak: 'break-all',
					whiteSpace: 'nowrap',
					minHeight: 18,
					'&:not([class*="MuiInputLabel-outlined"]):not([class*="MuiInputLabel-filled"])':
						{
							paddingLeft: defaultMuiTheme.spacing(2),
						},
				},
				filled: {
					color: light ? `rgba(0, 0, 0, 0.64)` : undefined,
				},
				outlined: {
					'&[class*="MuiInputLabel-shrink"]': {
						transform: `translate(14px, -7px) scale(0.75)`,
					},
				},
				shrink: {
					maxWidth: 'calc(100% * 1.25 - 15px) !important',
				},
			},
		},
		MuiInputBase: {
			defaultProps: {
				slotProps: {},
			},
			styleOverrides: {
				root: {
					// TODO: the override styles for `MuiOutlinedInput` below are not working
					// there is a bug ticket for this
					'@media (hover: none)': {
						'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
							borderColor: outlinedBorderColor,
						},
					},
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: outlinedBorderColor,
						'& legend': {
							overflow: 'hidden',
						},
					},
					'&.MuiInput-underline': {
						'& input': {
							paddingLeft: defaultMuiTheme.spacing(1.5),
						},
					},
				},
				input: {
					'&::-webkit-input-placeholder': placeholderVisible,
					'&::-moz-placeholder': placeholderVisible,
					'&:-ms-input-placeholder': placeholderVisible,
					'&::-ms-input-placeholder': placeholderVisible,
					'label[data-shrink=false] + $formControl &': {
						'&:focus::-webkit-input-placeholder': placeholderVisible,
						'&:focus::-moz-placeholder': placeholderVisible,
						'&:focus::-ms-input-placeholder': placeholderVisible,
					},
				},
			},
		},
		MuiOutlinedInput: {
			defaultProps: {
				notched: true,
			},
			styleOverrides: {
				root: {
					// Reset on touch devices, it doesn't add specificity
					'@media (hover: none)': {
						'&:hover $notchedOutline': {
							borderColor: outlinedBorderColor,
						},
					},
				},
				notchedOutline: {
					borderColor: outlinedBorderColor,
					'& legend': {
						overflow: 'hidden',
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: 'none',
				},
			},
			defaultProps: {
				elevation: light ? 2 : 5,
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						'& ~ svg': {
							opacity: 0.5,
						},
					},
				},
			},
		},
		MuiStepLabel: {
			styleOverrides: {
				root: {
					fontSize: 14,
					'& .MuiStepIcon-text': {
						fontSize: 12,
					},
					'& svg': {
						width: 24,
						height: 24,
					},
				},
				label: {
					fontSize: 14,
				},
			},
		},
		MuiSvgIcon: {
			styleOverrides: {
				root: {
					fontSize: 20,
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					// Messes with translations
					textTransform: 'unset',
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
				size: 'small',
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					fontSize: 14,
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					textAlign: 'center',
				},
			},
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					MozOsxFontSmoothing: 'grayscale',
					WebkitFontSmoothing: 'antialiased',
				},
				// TODO: For the font size descepency between theme and portal
				// using the 'px' value over 'rem' until we rectify
				body1: {
					fontSize: '16px',
				},
				body2: {
					fontSize: '14px',
				},
				caption: {
					fontSize: '12px',
				},
				h5: {
					fontSize: '24px',
				},
				h6: {
					fontSize: '18px',
				},
			},
		},
	};
};
